import React, { useState, useEffect } from 'react';
import { MdDownload } from 'react-icons/md';
import BBITable from '../../../helpers/bBITable/BBITable';
import DateOnlyFormatter from '../../../helpers/inputs/DateOnlyFormatter';
import FilterData from '../../../helpers/filter/FilterData';
import Get from '../../../api/internal/Get';
import Header from '../../../layouts/Header';
import View from '../../../helpers/slab/View';
import ViewVendor from '../../../features/views/vendor/Vendor';
import ViewLoad from '../../../features/views/load/Load';
import ViewPayable from '../../../features/views/payable/Payable';
import { useOutletContext } from 'react-router-dom';
import ViewRental from '../../../features/views/rental/Rental';
import { CSVLink } from 'react-csv';

function OutstandingPayablesBoard({
  toggleBoardRefresh,
}: {
  toggleBoardRefresh: boolean;
}) {
  const [payableBoard, setPayableBoard] = useState<Array<PayableBoard>>([]);
  const filterValue = useOutletContext();

  useEffect(() => {
    GetPayables();
  }, [toggleBoardRefresh]);

  function GetPayables() {
    Get(`/Accounting/GetPayableBoard/`).then(response => {
      if (response) {
        setPayableBoard(response.data);
      }
    });
  }

  function FormatPayableBoard(board) {
    return board.map(payable => ({
      ...payable,
      attribute: payable.loadId
        ? payable.loadId
        : payable.rentalId && 'Rental: ' + payable.rentalId,
      dueDate: DateOnlyFormatter(payable.dueDate),
      billDate: DateOnlyFormatter(payable.billDate),
      total: payable.total.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      }),
      remainingBalance: payable.remainingBalance.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      }),
    }));
  }

  const tableData = FilterData(FormatPayableBoard(payableBoard), filterValue);

  const columnConfig = [
    {
      key: '1',
      attribute: 'billName',
      attributeprimaryhidden: 'vendorAssignmentId',
      header: 'Bill Name',
      onClick: e =>
        View(
          e.target.dataset.attributeprimaryhidden && (
            <ViewPayable
              vendorAssignmentId={e.target.dataset.attributeprimaryhidden}
              loadId={e.target.dataset.attributesecondaryhidden}
              rentalId={e.target.dataset.attributetertiaryhidden}
              billName={e.target.innerText}
            />
          ),
        ),
      dataType: 'number',
    },
    {
      key: '1',
      attribute: 'attribute',
      attributeprimaryhidden: 'loadId',
      attributesecondaryhidden: 'rentalId',
      header: 'Load #',
      onClick: e =>
        View(
          e.target.dataset.attributeprimaryhidden ? (
            <ViewLoad loadId={e.target.dataset.attributeprimaryhidden} />
          ) : (
            e.target.dataset.attributesecondaryhidden && (
              <ViewRental
                rentalId={e.target.dataset.attributesecondaryhidden}
              />
            )
          ),
        ),
      dataType: 'number',
    },
    {
      key: '2',
      attribute: 'reference',
      attributeprimaryhidden: 'loadId',
      attributesecondaryhidden: 'rentalId',
      header: 'Reference',
      onClick: e =>
        View(
          e.target.dataset.attributeprimaryhidden ? (
            <ViewLoad loadId={e.target.dataset.attributeprimaryhidden} />
          ) : (
            e.target.dataset.attributesecondaryhidden && (
              <ViewRental
                rentalId={e.target.dataset.attributesecondaryhidden}
              />
            )
          ),
        ),
    },
    {
      key: '3',
      attribute: 'paymentStatus',
      attributeprimaryhidden: 'vendorAssignmentId',
      attributesecondaryhidden: 'loadId',
      attributetertiaryhidden: 'rentalId',
      header: 'Status',
      onClick: e =>
        View(
          <ViewPayable
            vendorAssignmentId={e.target.dataset.attributeprimaryhidden}
            loadId={e.target.dataset.attributesecondaryhidden}
            rentalId={e.target.dataset.attributetertiaryhidden}
            billName={e.target.innerText}
          />,
        ),
      dataType: 'string',
    },
    {
      key: '4',
      attribute: 'vendorName',
      attributeprimaryhidden: 'vendorId',
      header: 'Vendor',
      onClick: e =>
        View(
          <ViewVendor
            vendorName={e.target.innerText}
            vendorId={e.target.dataset.attributeprimaryhidden}
          />,
        ),
    },
    {
      key: '5',
      attribute: 'quickBooksBillId',
      header: 'QuickBooks Bill ID',
    },
    {
      key: '6',
      attribute: 'billDate',
      header: 'Bill Date',
      dataType: 'date',
    },
    {
      key: '7',
      attribute: 'dueDate',
      header: 'Due Date',
      dataType: 'date',
    },
    {
      key: '8',
      attribute: 'total',
      header: 'Total',
      dataType: 'currency',
    },
    {
      key: '9',
      attribute: 'remainingBalance',
      header: 'Remaining Balance',
      dataType: 'currency',
    },
  ];

  const csvFileName =
    new Date().toISOString().split('T')[0].replace(/[-]/g, '') +
    '_Payables Report.csv';

  return (
    <>
      <Header>
        <div>
          <button>
            <CSVLink
              data={tableData}
              headers={[
                { label: 'Bill Name', key: 'billName' },
                { label: 'Load #', key: 'attribute' },
                { label: 'Reference', key: 'reference' },
                { label: 'Status', key: 'paymentStatus' },
                { label: 'Vendor', key: 'vendorName' },
                { label: 'QuickBooks Bill ID', key: 'quickBooksBillId' },
                { label: 'Bill Date', key: 'billDate' },
                { label: 'Due Date', key: 'dueDate' },
                { label: 'Total', key: 'total' },
                { label: 'Remaining Balance', key: 'remainingBalance' },
              ]}
              filename={csvFileName}
            >
              <MdDownload />
              <span>Download</span>
            </CSVLink>
          </button>
        </div>
      </Header>
      <BBITable data={tableData} columns={columnConfig} />
    </>
  );
}

export default OutstandingPayablesBoard;
